import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Zap, Flame, Wifi, ArrowRight, ArrowLeft, Phone, Mail, User, Shield, Loader2,
  CheckCircle2, Star, Sparkles, TrendingDown, Award, HelpCircle, HeartHandshake,
  MessageCircle, Clock, Users, MapPin,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const trackEvent = (event, extra = {}) => {
  try {
    axios.post(`${API_URL}/api/public/analytics`, {
      event, path: '/', referrer: typeof document !== 'undefined' ? document.referrer : '', ...extra,
    }).catch(() => {});
  } catch { /* ignore */ }
};

// --- Offerte placeholder (verranno collegate al motore reale in seguito) ---
// Fallback features generiche quando il backend non ritorna niente
const FALLBACK_FEATURES = {
  luce: ['Prezzo bloccato', 'Attivazione gratuita', 'App di controllo consumi'],
  gas: ['Prezzo bloccato', 'Nessun deposito cauzionale', 'Bolletta chiara'],
  fibra: ['Fino a 1 Gbps', 'Modem incluso', 'Attivazione gratuita'],
};

// ============ MAIN ============
export default function PublicQuote() {
  const [tab, setTab] = useState('luce');
  const [step, setStep] = useState(1); // 1 dati utenza, 2 risultati, 3 lead form, 4 done
  const [offers, setOffers] = useState([]);
  const [offersMeta, setOffersMeta] = useState({ has_savings: false, stima_consumo: null });
  const [fetchingOffers, setFetchingOffers] = useState(false);
  const [form, setForm] = useState({
    cap: '', comune: '', tipo_utenza: 'domestico',
    consumo_annuo_kwh: '', potenza_kw: '', n_persone: '', dontKnowLuce: false,
    consumo_annuo_smc: '', n_fornelli: '', mq: '', dontKnowGas: false,
    operatore_attuale: '',
  });
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [lead, setLead] = useState({ nome: '', cognome: '', telefono: '', email: '', note: '' });
  const [privacy, setPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const resultsRef = useRef(null);

  useEffect(() => { trackEvent('pageview'); }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const updL = (k) => (e) => setLead({ ...lead, [k]: e.target.value });

  const scrollToPrev = () => {
    document.getElementById('preventivatore')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const goResults = async (e) => {
    e?.preventDefault();
    if (!form.cap || form.cap.length < 5) { toast.error('Inserisci un CAP valido'); return; }
    setFetchingOffers(true);
    setOffers([]);
    try {
      const payload = {
        servizio: tab,
        tipo_utenza: form.tipo_utenza,
        cap: form.cap || null,
      };
      if (tab === 'luce') {
        if (form.consumo_annuo_kwh) payload.consumo_annuo_kwh = Number(form.consumo_annuo_kwh);
        if (form.potenza_kw) payload.potenza_kw = Number(form.potenza_kw);
        if (form.n_persone) payload.n_persone = Number(form.n_persone);
      } else if (tab === 'gas') {
        if (form.consumo_annuo_smc) payload.consumo_annuo_smc = Number(form.consumo_annuo_smc);
        if (form.n_fornelli) payload.n_fornelli = Number(form.n_fornelli);
        if (form.mq) payload.mq = Number(form.mq);
      } else if (tab === 'fibra' && form.operatore_attuale) {
        payload.operatore_attuale = form.operatore_attuale;
      }
      const res = await axios.post(`${API_URL}/api/public/preventivatore/offerte`, payload);
      setOffers(res.data.offers || []);
      setOffersMeta({ has_savings: res.data.has_savings, stima_consumo: res.data.stima_consumo });
      setStep(2);
      trackEvent('form_submit', { lead_id: null });
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Impossibile recuperare le offerte, riprova.');
    } finally {
      setFetchingOffers(false);
    }
  };

  const pickOffer = (offer) => {
    setSelectedOffer(offer);
    setStep(3);
    setTimeout(() => document.getElementById('lead-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 120);
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.nome || !lead.cognome || !lead.telefono) { toast.error('Nome, cognome e telefono sono obbligatori'); return; }
    if (!privacy) { toast.error('Accetta l\'informativa privacy'); return; }
    setLoading(true);
    try {
      const payload = {
        servizio: tab,
        cap: form.cap || null, comune: form.comune || null, tipo_utenza: form.tipo_utenza,
        consumo_annuo_kwh: form.consumo_annuo_kwh ? Number(form.consumo_annuo_kwh) : null,
        potenza_kw: form.potenza_kw ? Number(form.potenza_kw) : null,
        n_persone: form.n_persone ? Number(form.n_persone) : null,
        consumo_annuo_smc: form.consumo_annuo_smc ? Number(form.consumo_annuo_smc) : null,
        n_fornelli: form.n_fornelli ? Number(form.n_fornelli) : null,
        mq: form.mq ? Number(form.mq) : null,
        operatore_attuale: form.operatore_attuale || null,
        nome: lead.nome, cognome: lead.cognome, telefono: lead.telefono,
        email: lead.email || null, note: lead.note || null,
        offerta_scelta: selectedOffer?.offerta_nome, offerta_prezzo: formatOfferPrice(selectedOffer, tab),
      };
      const res = await axios.post(`${API_URL}/api/public/landing-lead`, payload);
      trackEvent('offer_accept', { lead_id: res.data.lead_id });
      setStep(4);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  // Helper per formattare prezzo offerta
  const formatOfferPrice = (o, servizio) => {
    if (!o) return '';
    if (servizio === 'fibra') return `${(o.prezzo_mensile || 0).toFixed(2)} €/mese`;
    const unit = servizio === 'luce' ? '€/kWh' : '€/Smc';
    return o.prezzo_unitario ? `${o.prezzo_unitario.toFixed(4)} ${unit}` : '';
  };

  return (
    <div className="min-h-[100dvh] bg-[#0a1628] text-slate-100 antialiased relative overflow-x-hidden overflow-x-clip">
      {/* Base bg gradient */}
      <div className="fixed inset-0 bg-gradient-to-br from-[#0a1628] via-[#0d1f3c] to-[#0a0f1c] pointer-events-none" />
      <div className="fixed top-1/4 -left-20 w-96 h-96 bg-sky-500/10 rounded-full blur-[100px] pointer-events-none" />
      <div className="fixed bottom-1/4 right-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-[100px] pointer-events-none translate-x-1/3" />

      {/* HEADER */}
      <header className="relative z-30 border-b border-white/5 backdrop-blur bg-[#0a1628]/80 sticky top-0 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-sky-500/20 border border-sky-500/30 backdrop-blur-sm">
              <Zap className="w-5 h-5 text-sky-400" />
            </div>
            <div className="leading-tight">
              <div className="font-bold text-sm sm:text-base tracking-tight">F.lli Schilirò Servizi</div>
              <div className="text-[10px] sm:text-xs text-sky-300/70 -mt-0.5">Energia · Gas · Fibra</div>
            </div>
          </Link>
          <nav className="flex items-center gap-2">
            <Link to="/login" data-testid="nav-area-agente"
              className="text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg border border-white/10 hover:border-sky-400/40 hover:bg-sky-500/5 transition-colors text-slate-200">
              Area Agente
            </Link>
            <Link to="/clienti/login" data-testid="nav-area-cliente"
              className="text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg bg-sky-500 hover:bg-sky-400 text-white font-semibold transition-colors shadow-lg shadow-sky-500/20">
              Area Cliente
            </Link>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="relative z-10 pt-10 sm:pt-20 pb-10 sm:pb-16">
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.06) 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
            maskImage: 'radial-gradient(ellipse at center, black 20%, transparent 70%)',
            WebkitMaskImage: 'radial-gradient(ellipse at center, black 20%, transparent 70%)',
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-300 mb-6">
            <Sparkles className="w-3.5 h-3.5" />
            Comparatore AI · Preventivo gratuito in meno di 2 minuti
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] max-w-4xl mx-auto">
            Calcola il tuo preventivo <br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-sky-400 to-cyan-300">
              luce, gas e fibra
            </span>{' '}
            in pochi secondi
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-400 max-w-2xl mx-auto">
            Confrontiamo decine di offerte in tempo reale e ti proponiamo quella giusta per i tuoi consumi.
            Nessun impegno, nessun venditore insistente.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button data-testid="hero-cta" onClick={scrollToPrev}
              className="h-12 px-7 text-base bg-sky-500 hover:bg-sky-400 text-white font-semibold shadow-lg shadow-sky-500/20">
              Inizia il preventivo
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <Shield className="w-4 h-4 text-emerald-400" />
              Dati trattati nel rispetto del GDPR
            </div>
          </div>
          {/* Stats row */}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-x-10 sm:gap-x-16 gap-y-5 text-slate-200">
            {[{ num: '500+', label: 'Lead gestiti' }, { num: '98%', label: 'Soddisfazione' }, { num: '50+', label: 'Agenti attivi' }, { num: '18+', label: 'Fornitori partner' }].map((s) => (
              <div key={s.label}>
                <div className="text-2xl sm:text-3xl font-bold">{s.num}</div>
                <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PREVENTIVATORE */}
      <section id="preventivatore" className="relative z-10 py-10 sm:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <div className="text-xs uppercase tracking-[0.2em] text-sky-300 font-semibold">Il tuo preventivo</div>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold">Scegli il servizio e inizia</h2>
          </div>

          {/* Tabs servizio */}
          <div className="flex flex-col sm:flex-row items-stretch gap-2 mb-6">
            {[
              { k: 'luce', Icon: Zap, label: 'Luce', color: 'from-amber-400 to-orange-500' },
              { k: 'gas', Icon: Flame, label: 'Gas', color: 'from-sky-400 to-cyan-500' },
              { k: 'fibra', Icon: Wifi, label: 'Fibra', color: 'from-fuchsia-400 to-rose-500' },
            ].map(({ k, Icon, label, color }) => {
              const active = tab === k;
              return (
                <button
                  key={k}
                  data-testid={`tab-${k}`}
                  onClick={() => { setTab(k); setStep(1); setSelectedOffer(null); }}
                  className={`flex-1 flex items-center justify-center gap-2 h-14 rounded-xl border font-semibold transition-all ${
                    active
                      ? 'bg-sky-500/15 border-sky-400/60 text-white shadow-lg shadow-sky-500/10'
                      : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:text-slate-200'
                  }`}
                >
                  <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${color} grid place-items-center ${active ? '' : 'opacity-70'}`}>
                    <Icon className="w-4 h-4 text-slate-950" />
                  </div>
                  {label}
                </button>
              );
            })}
          </div>

          {/* Step 1: Form dati utenza */}
          {step === 1 && (
            <Card className="bg-slate-900/70 border-white/10 backdrop-blur">
              <CardContent className="p-6 sm:p-8">
                <form onSubmit={goResults} className="space-y-5">
                  {/* CAP + Comune + Tipo (comuni a tutti) */}
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-slate-300 text-sm">CAP *</Label>
                      <div className="relative mt-1">
                        <MapPin className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                        <Input value={form.cap} onChange={upd('cap')} data-testid="input-cap" maxLength={5}
                          className="pl-9 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-500" placeholder="20121" required />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-300 text-sm">Comune</Label>
                      <Input value={form.comune} onChange={upd('comune')} data-testid="input-comune"
                        className="mt-1 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-500" placeholder="Milano" />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-sm">Tipologia</Label>
                      <div className="grid grid-cols-2 gap-1 mt-1">
                        {['domestico', 'business'].map((t) => (
                          <button
                            key={t} type="button" data-testid={`tipo-${t}`}
                            onClick={() => setForm({ ...form, tipo_utenza: t })}
                            className={`h-10 rounded-lg text-xs font-semibold capitalize transition-colors ${
                              form.tipo_utenza === t
                                ? 'bg-sky-500/15 border border-sky-400/60 text-white'
                                : 'bg-white/5 border border-white/10 text-slate-400 hover:bg-white/10'
                            }`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* LUCE */}
                  {tab === 'luce' && (
                    <div className="space-y-4 pt-2 border-t border-white/5">
                      {!form.dontKnowLuce ? (
                        <div className="flex flex-col sm:flex-row gap-3">
                          <div className="flex-1">
                            <Label className="text-slate-300 text-sm">Consumo annuo stimato (kWh)</Label>
                            <Input type="number" value={form.consumo_annuo_kwh} onChange={upd('consumo_annuo_kwh')} data-testid="input-consumo-luce"
                              className="mt-1 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-500" placeholder="Es. 2.700 kWh/anno" />
                          </div>
                          <button type="button" data-testid="dont-know-luce"
                            onClick={() => setForm({ ...form, dontKnowLuce: true, consumo_annuo_kwh: '' })}
                            className="self-end h-10 px-4 rounded-lg bg-sky-500/10 border border-sky-500/30 text-sky-300 text-sm font-medium hover:bg-sky-500/20 inline-flex items-center gap-1.5">
                            <HelpCircle className="w-4 h-4" /> Non lo so, aiutami a stimarlo
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="text-sm text-sky-300 flex items-center gap-2">
                            <Sparkles className="w-4 h-4" /> Rispondi a 2 domande, stimiamo noi il consumo
                          </div>
                          <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                              <Label className="text-slate-300 text-sm">Potenza impegnata (kW)</Label>
                              <Input type="number" step="0.5" value={form.potenza_kw} onChange={upd('potenza_kw')} data-testid="input-potenza"
                                className="mt-1 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-500" placeholder="Es. 3.0 o 4.5" />
                            </div>
                            <div>
                              <Label className="text-slate-300 text-sm">Numero di persone in casa</Label>
                              <Input type="number" value={form.n_persone} onChange={upd('n_persone')} data-testid="input-persone"
                                className="mt-1 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-500" placeholder="Es. 3" />
                            </div>
                          </div>
                          <button type="button" onClick={() => setForm({ ...form, dontKnowLuce: false })}
                            className="text-xs text-slate-400 underline hover:text-slate-200">
                            So quanti kWh consumo
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* GAS */}
                  {tab === 'gas' && (
                    <div className="space-y-4 pt-2 border-t border-white/5">
                      {!form.dontKnowGas ? (
                        <div className="flex flex-col sm:flex-row gap-3">
                          <div className="flex-1">
                            <Label className="text-slate-300 text-sm">Consumo annuo stimato (Smc)</Label>
                            <Input type="number" value={form.consumo_annuo_smc} onChange={upd('consumo_annuo_smc')} data-testid="input-consumo-gas"
                              className="mt-1 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-500" placeholder="Es. 1.400 Smc/anno" />
                          </div>
                          <button type="button" data-testid="dont-know-gas"
                            onClick={() => setForm({ ...form, dontKnowGas: true, consumo_annuo_smc: '' })}
                            className="self-end h-10 px-4 rounded-lg bg-sky-500/10 border border-sky-500/30 text-sky-300 text-sm font-medium hover:bg-sky-500/20 inline-flex items-center gap-1.5">
                            <HelpCircle className="w-4 h-4" /> Non lo so, aiutami a stimarlo
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="text-sm text-sky-300 flex items-center gap-2">
                            <Sparkles className="w-4 h-4" /> Rispondi a 2 domande, stimiamo noi il consumo
                          </div>
                          <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                              <Label className="text-slate-300 text-sm">Numero di fornelli</Label>
                              <Input type="number" value={form.n_fornelli} onChange={upd('n_fornelli')} data-testid="input-fornelli"
                                className="mt-1 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-500" placeholder="Es. 4" />
                            </div>
                            <div>
                              <Label className="text-slate-300 text-sm">Metri quadri casa</Label>
                              <Input type="number" value={form.mq} onChange={upd('mq')} data-testid="input-mq"
                                className="mt-1 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-500" placeholder="Es. 90" />
                            </div>
                          </div>
                          <button type="button" onClick={() => setForm({ ...form, dontKnowGas: false })}
                            className="text-xs text-slate-400 underline hover:text-slate-200">
                            So quanti Smc consumo
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* FIBRA */}
                  {tab === 'fibra' && (
                    <div className="pt-2 border-t border-white/5">
                      <Label className="text-slate-300 text-sm">Operatore attuale (opzionale)</Label>
                      <Input value={form.operatore_attuale} onChange={upd('operatore_attuale')} data-testid="input-operatore"
                        className="mt-1 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-500" placeholder="Es. TIM, Vodafone, Fastweb..." />
                    </div>
                  )}

                  <Button type="submit" data-testid="btn-vedi-offerte" disabled={fetchingOffers}
                    className="w-full h-12 text-base bg-sky-500 hover:bg-sky-400 text-white font-semibold shadow-lg shadow-sky-500/20">
                    {fetchingOffers ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cerco le offerte migliori...</>
                    ) : (
                      <>Vedi le offerte per te<ArrowRight className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Risultati offerte */}
          {step >= 2 && (
            <div ref={resultsRef} className="space-y-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-wider text-sky-300 font-semibold">Risultati</div>
                  <h3 className="text-xl sm:text-2xl font-bold mt-1">
                    {offersMeta.has_savings && offers.some((o) => o.risparmio_stimato_annuo > 0)
                      ? <>Puoi risparmiare fino a <span className="text-emerald-300">{Math.round(Math.max(...offers.map(o => o.risparmio_stimato_annuo || 0)))}€/anno</span></>
                      : 'Le offerte migliori per te'}
                  </h3>
                  {offersMeta.stima_consumo && (
                    <p className="text-xs text-slate-500 mt-1">
                      Stima basata su un consumo di <b className="text-slate-300">{offersMeta.stima_consumo}</b> {tab === 'luce' ? 'kWh' : 'Smc'}/anno
                    </p>
                  )}
                </div>
                <button data-testid="back-to-form" onClick={() => { setStep(1); setSelectedOffer(null); setOffers([]); }}
                  className="text-sm text-slate-400 hover:text-slate-200 inline-flex items-center gap-1">
                  <ArrowLeft className="w-4 h-4" /> Modifica dati
                </button>
              </div>
              {offers.length > 0 ? (
                <div className="grid md:grid-cols-3 gap-4">
                  {offers.map((o, i) => (
                    <OfferCard key={o.offerta_id || i} offer={o} servizio={tab} featured={i === 0} onPick={() => pickOffer(o)} testid={`offer-${i}`} />
                  ))}
                </div>
              ) : (
                <Card className="bg-slate-900/70 border-white/10">
                  <CardContent className="p-10 text-center">
                    <div className="text-slate-300">Al momento non abbiamo offerte attive per questa combinazione.</div>
                    <div className="text-sm text-slate-500 mt-2">Lasciaci i tuoi dati: un consulente ti proporrà offerte dedicate.</div>
                    <Button data-testid="no-offer-contact" onClick={() => { setSelectedOffer({ offerta_nome: 'Richiesta generica' }); setStep(3); }}
                      className="mt-5 bg-sky-500 hover:bg-sky-400 text-white font-semibold">
                      Fatti ricontattare <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Step 3: Lead form */}
          {step === 3 && (
            <Card id="lead-form" className="mt-6 bg-slate-900/70 border-white/10 backdrop-blur">
              <CardContent className="p-6 sm:p-8">
                <div className="flex items-start justify-between gap-3 mb-5">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-sky-300 font-semibold">Ultimo passo</div>
                    <h3 className="text-xl sm:text-2xl font-bold mt-1">
                      Richiedi: <span className="text-sky-400">{selectedOffer?.offerta_nome}</span>
                    </h3>
                    <p className="text-sm text-slate-400 mt-1">
                      {[selectedOffer?.compagnia, formatOfferPrice(selectedOffer, tab)].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <button data-testid="back-to-offers" onClick={() => { setStep(2); setSelectedOffer(null); }} className="text-slate-500 hover:text-slate-200">
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                </div>
                <form onSubmit={submitLead} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-300 text-sm">Nome *</Label>
                      <div className="relative mt-1">
                        <User className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                        <Input value={lead.nome} onChange={updL('nome')} data-testid="lead-nome" required
                          className="pl-9 bg-slate-950/50 border-white/10 text-white" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-300 text-sm">Cognome *</Label>
                      <div className="relative mt-1">
                        <User className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                        <Input value={lead.cognome} onChange={updL('cognome')} data-testid="lead-cognome" required
                          className="pl-9 bg-slate-950/50 border-white/10 text-white" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-300 text-sm">Telefono *</Label>
                      <div className="relative mt-1">
                        <Phone className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                        <Input value={lead.telefono} onChange={updL('telefono')} data-testid="lead-telefono" required
                          className="pl-9 bg-slate-950/50 border-white/10 text-white" placeholder="+39 ..." />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-300 text-sm">Email</Label>
                      <div className="relative mt-1">
                        <Mail className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                        <Input type="email" value={lead.email} onChange={updL('email')} data-testid="lead-email"
                          className="pl-9 bg-slate-950/50 border-white/10 text-white" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">Note (opzionale)</Label>
                    <Textarea value={lead.note} onChange={updL('note')} data-testid="lead-note" rows={2}
                      className="mt-1 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-500" placeholder="Richieste particolari" />
                  </div>
                  <label className="flex items-start gap-3 pt-1 cursor-pointer">
                    <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} data-testid="lead-privacy"
                      className="mt-1 w-4 h-4 rounded border-white/20 bg-slate-950 accent-sky-400" />
                    <span className="text-xs text-slate-400 leading-relaxed">
                      Accetto il trattamento dei miei dati per essere ricontattato. Vedi{' '}
                      <Link to="/privacy" className="text-sky-300 underline">Privacy Policy</Link>.
                    </span>
                  </label>
                  <Button type="submit" disabled={loading} data-testid="lead-submit"
                    className="w-full h-12 bg-sky-500 hover:bg-sky-400 text-white font-semibold shadow-lg shadow-sky-500/20">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Richiedi questa offerta <ArrowRight className="w-4 h-4 ml-2" /></>)}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Step 4: Done */}
          {step === 4 && (
            <Card className="bg-emerald-500/5 border-emerald-500/30">
              <CardContent className="p-8 sm:p-12 text-center" data-testid="done-block">
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/20 border border-emerald-400/40 grid place-items-center">
                  <CheckCircle2 className="w-8 h-8 text-emerald-300" />
                </div>
                <h3 className="mt-5 text-2xl sm:text-3xl font-bold">Richiesta ricevuta!</h3>
                <p className="mt-3 text-slate-300 max-w-md mx-auto">
                  Un consulente ti contatterà a breve per attivare <b className="text-white">{selectedOffer?.offerta_nome}</b>{selectedOffer?.compagnia ? <> di <b className="text-white">{selectedOffer.compagnia}</b></> : null}.
                </p>
                <div className="mt-6 inline-flex items-center gap-2 text-xs text-slate-400 bg-white/5 border border-white/10 px-4 py-2 rounded-full">
                  <Clock className="w-3.5 h-3.5" />
                  Risposta tipica entro 24 ore lavorative (lun-ven)
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* BADGES / PUNTI DI FORZA */}
      <section className="relative z-10 py-14 sm:py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.2em] text-sky-300 font-semibold">Perché sceglierci</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold">Trasparenza, risparmio reale, zero stress</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { Icon: Sparkles, title: 'Comparatore AI', body: 'La nostra intelligenza artificiale confronta decine di offerte in tempo reale.' },
              { Icon: Users, title: 'Oltre 500 lead gestiti', body: 'Famiglie e aziende che si fidano del nostro lavoro ogni giorno.' },
              { Icon: Award, title: '98% soddisfazione', body: 'La nostra rete di consulenti è valutata al top dai nostri clienti.' },
              { Icon: HeartHandshake, title: 'Zero costi nascosti', body: 'Nessun impegno di spesa, nessun costo di attivazione extra per te.' },
              { Icon: Shield, title: 'Partner certificati', body: '18+ fornitori nazionali tra energia e telecomunicazioni.' },
              { Icon: MessageCircle, title: 'Supporto umano', body: 'Un consulente dedicato dalla richiesta all’attivazione.' },
            ].map(({ Icon, title, body }, i) => (
              <div key={title} data-testid={`feature-${i}`}
                className="rounded-2xl bg-slate-900/60 border border-white/10 p-5 hover:border-sky-400/30 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-sky-500/15 border border-sky-500/20 grid place-items-center">
                  <Icon className="w-5 h-5 text-sky-400" />
                </div>
                <div className="mt-4 font-semibold">{title}</div>
                <div className="text-sm text-slate-400 mt-1">{body}</div>
              </div>
            ))}
          </div>
          {/* Rating pill */}
          <div className="mt-10 flex items-center justify-center">
            <div className="flex items-center gap-3 bg-slate-900/70 border border-white/10 rounded-full px-5 py-2.5">
              <div className="flex items-center gap-0.5">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-300 text-amber-300" />
                ))}
              </div>
              <div className="text-sm text-white font-semibold">4,9/5</div>
              <div className="text-xs text-slate-400">su oltre 420 recensioni verificate</div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="relative z-10 border-t border-white/5 py-10 sm:py-14 bg-[#07101e]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid sm:grid-cols-3 gap-8 text-sm">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-sky-500/20 border border-sky-500/30">
                <Zap className="w-4 h-4 text-sky-400" />
              </div>
              <div className="font-bold text-white">F.lli Schilirò Servizi</div>
            </div>
            <p className="text-slate-400 text-xs leading-relaxed">
              Consulenti indipendenti del mercato dell'energia e delle telecomunicazioni. Lavoriamo solo
              quando risparmi davvero.
            </p>
          </div>
          <div>
            <div className="text-sky-300 font-semibold text-xs uppercase tracking-wider mb-3">Contatti</div>
            <div className="space-y-2 text-slate-300">
              <a href="tel:+390957938069" className="flex items-center gap-2 hover:text-white" data-testid="footer-tel">
                <Phone className="w-4 h-4 text-sky-400" /> 095 7938069
              </a>
              <a href="mailto:assicuratiramacca@gmail.com" className="flex items-center gap-2 hover:text-white break-all" data-testid="footer-email">
                <Mail className="w-4 h-4 text-sky-400" /> assicuratiramacca@gmail.com
              </a>
            </div>
          </div>
          <div>
            <div className="text-sky-300 font-semibold text-xs uppercase tracking-wider mb-3">Orari</div>
            <div className="space-y-1 text-slate-300 text-xs">
              <div><b className="text-slate-200">Lun-Ven</b> · 9:30 - 13:00</div>
              <div><b className="text-slate-200">Lun-Ven</b> · 16:30 - 19:30</div>
              <div className="pt-2 flex gap-3">
                <Link to="/privacy" className="hover:text-white underline">Privacy Policy</Link>
                <Link to="/termini" className="hover:text-white underline">Termini di Servizio</Link>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-10 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} F.lli Schilirò Servizi · Tutti i diritti riservati
        </div>
      </footer>
    </div>
  );
}

// ============ Offer Card ============
const OfferCard = ({ offer, servizio, featured, onPick, testid }) => {
  const features = (offer.features || []).filter(Boolean);
  if (features.length === 0) {
    (FALLBACK_FEATURES[servizio] || []).forEach((f) => features.push(f));
  }
  const risparmio = offer.risparmio_stimato_annuo || 0;
  const badge = featured ? (risparmio > 0 ? 'Più scelta · Risparmi di più' : 'Consigliata') : null;

  // Prezzo + unità per servizio
  let prezzoNum = null;
  let prezzoUnit = '';
  if (servizio === 'fibra') {
    prezzoNum = offer.prezzo_mensile;
    prezzoUnit = '€/mese';
  } else {
    prezzoNum = offer.prezzo_unitario;
    prezzoUnit = servizio === 'luce' ? '€/kWh' : '€/Smc';
  }

  return (
    <div
      data-testid={testid}
      className={`relative rounded-2xl border p-5 transition-all flex flex-col ${
        featured
          ? 'bg-gradient-to-br from-sky-500/15 to-cyan-500/5 border-sky-400/50 shadow-lg shadow-sky-500/10'
          : 'bg-slate-900/70 border-white/10'
      }`}
    >
      {badge && (
        <div className={`absolute -top-3 left-5 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${
          featured ? 'bg-sky-400 text-slate-950' : 'bg-slate-800 text-sky-200 border border-sky-400/30'
        }`}>
          {badge}
        </div>
      )}
      <div className="flex items-start gap-2.5">
        {offer.compagnia_logo ? (
          <img src={offer.compagnia_logo} alt={offer.compagnia} className="w-9 h-9 rounded-lg object-contain bg-white p-0.5 shrink-0" />
        ) : (
          <div className="w-9 h-9 rounded-lg bg-sky-500/15 border border-sky-500/30 grid place-items-center shrink-0">
            <Zap className="w-4 h-4 text-sky-400" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-xs text-slate-400 truncate">{offer.compagnia || 'Partner'}</div>
          <div className="font-bold text-base leading-tight mt-0.5 truncate">{offer.offerta_nome}</div>
        </div>
      </div>

      <div className="mt-4 flex items-end gap-1">
        <div className="text-3xl font-extrabold text-sky-300">
          {prezzoNum != null ? prezzoNum.toFixed(servizio === 'fibra' ? 2 : 4).replace('.', ',') : '—'}
        </div>
        <div className="text-xs text-slate-400 mb-1">{prezzoUnit}</div>
      </div>

      {risparmio > 0 && (
        <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-300">
          <TrendingDown className="w-3.5 h-3.5" />
          Risparmio stimato: <span className="font-bold">{Math.round(risparmio)}€/anno</span>
          {offer.risparmio_pct ? <span className="text-emerald-400/80">(−{Math.round(offer.risparmio_pct)}%)</span> : null}
        </div>
      )}

      {offer.durata_offerta_mesi && (
        <div className="text-xs text-slate-500 mt-2">Durata: {offer.durata_offerta_mesi} mesi</div>
      )}
      {servizio === 'fibra' && offer.velocita_download && (
        <div className="text-xs text-slate-500 mt-1">Velocità: fino a {offer.velocita_download} Mbps</div>
      )}

      <ul className="mt-4 space-y-1.5 text-xs text-slate-300 flex-1">
        {features.slice(0, 3).map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Button
        data-testid={`${testid}-pick`}
        onClick={onPick}
        className={`w-full mt-5 h-10 text-sm font-semibold ${
          featured
            ? 'bg-sky-500 hover:bg-sky-400 text-white'
            : 'bg-white/10 hover:bg-white/20 text-white border border-white/10'
        }`}
      >
        Richiedi questa offerta
        <ArrowRight className="w-4 h-4 ml-1.5" />
      </Button>
    </div>
  );
};
